import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { updateBusiness } from '../services/userManagementService';
import BusinessFormFields from './BusinessFormFields';

const EMPTY_BUSINESS_FORM = {
    businessName: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: '',
    country: '',
    phoneNumber: '',
    email: '',
};

const cleanPhoneNumber = (phoneNumber) => (phoneNumber || '').replace(/[^0-9]/g, '');

const handleDigitsOnlyChange = (field, value, setter) => {
    if (!/^[0-9]*$/.test(value)) return;
    setter(field, value);
};

const validateBusinessForm = (values) => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!values.businessName.trim()) errors.businessName = 'Company name is required';
    if (values.email.trim() && !emailRegex.test(values.email))
        errors.email = 'Invalid email format';

    return errors;
};

const businessFormFromUser = (user) => {
    if (!user) return EMPTY_BUSINESS_FORM;
    return {
        businessName: user.businessName || '',
        streetAddress: user.businessStreetAddress || '',
        city: user.businessCity || '',
        province: user.businessProvince || '',
        postalCode: user.businessPostalCode || '',
        country: user.businessCountry || '',
        phoneNumber: user.businessPhone || '',
        email: user.businessEmail || '',
    };
};

const EditBusinessDialog = ({ open, onClose, user, onSaveComplete }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [businessFormData, setBusinessFormData] = useState(EMPTY_BUSINESS_FORM);
    const [businessErrors, setBusinessErrors] = useState({});
    const [businessLoading, setBusinessLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setBusinessFormData(businessFormFromUser(user));
            setBusinessErrors({});
            setError('');
        }
    }, [open, user]);

    if (!user) return null;

    const handleSaveBusinessDetails = async () => {
        const errors = validateBusinessForm(businessFormData);
        setBusinessErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setBusinessLoading(true);
        setError('');

        try {
            const updatedBusiness = await updateBusiness(user.businessId, {
                businessId: user.businessId,
                businessName: businessFormData.businessName.trim(),
                streetAddress: businessFormData.streetAddress.trim(),
                city: businessFormData.city.trim(),
                province: businessFormData.province.trim(),
                postalCode: businessFormData.postalCode.trim(),
                country: businessFormData.country.trim(),
                phoneNumber: cleanPhoneNumber(businessFormData.phoneNumber),
                email: businessFormData.email.trim(),
            });

            onSaveComplete(updatedBusiness);
        } catch (err) {
            if (err.response?.data && typeof err.response.data === 'string') {
                setError(err.response.data);
            } else {
                setError('Failed to update company details. Please try again.');
            }
        } finally {
            setBusinessLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
            <DialogTitle>Edit Company Details</DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Alert severity="warning">
                        These changes update the company record and affect every user assigned to
                        this company.
                    </Alert>
                    <BusinessFormFields
                        values={businessFormData}
                        onChange={(field, value) => {
                            setBusinessFormData((prev) => ({ ...prev, [field]: value }));
                            setBusinessErrors((prev) => ({ ...prev, [field]: undefined }));
                        }}
                        onPhoneChange={(field, value) =>
                            handleDigitsOnlyChange(field, value, (key, nextValue) => {
                                setBusinessFormData((prev) => ({
                                    ...prev,
                                    [key]: nextValue,
                                }));
                                setBusinessErrors((prev) => ({ ...prev, [key]: undefined }));
                            })
                        }
                        errors={businessErrors}
                        required={false}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={businessLoading} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSaveBusinessDetails}
                    disabled={businessLoading}
                    startIcon={businessLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                    sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                    {businessLoading ? 'Saving...' : 'Save Company'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditBusinessDialog;
